import React, { useEffect, useState, useCallback, useRef } from "react";
import { PlaidLinkOnExit } from "react-plaid-link";
import { usePlaidLinkWithLogging } from "../hooks/usePlaidLinkWithLogging";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  useToast,
  Spinner,
  Center,
  useColorModeValue,
} from "@chakra-ui/react";
import { apiFetch, apiJson } from "../lib/api";
import { useUser } from "../Context/userContext";
import { useAuthContext } from "../contexts/AuthContext";

// Keys must match ConnectedBanksList and PlaidOAuthReturn.
const RECONNECT_LINK_TOKEN_KEY = "xacto_plaid_reconnect_link_token";
const RECONNECT_TOKEN_ID_KEY = "xacto_plaid_reconnect_token_id";
const OAUTH_PENDING_KEY = "xacto_plaid_oauth_pending";

export const ReconnectPlaidPage: React.FC = () => {
  const isExchangingRef = useRef(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedTenant } = useUser();
  const { session } = useAuthContext();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });
  const hasSession = !!session?.access_token;
  const [searchParams] = useSearchParams();

  const tokenIdFromUrl = searchParams.get("tokenId");

  // Detect whether the bank has redirected back after OAuth consent.
  // Computed once at render — stable for the lifetime of this page load.
  const isOAuthReturn = window.location.href.includes("?oauth_state_id=");

  // On OAuth return the URL loses the tokenId query param.
  // Restore it from localStorage where we saved it before opening Link.
  const effectiveTokenId = isOAuthReturn
    ? (tokenIdFromUrl || localStorage.getItem(RECONNECT_TOKEN_ID_KEY))
    : tokenIdFromUrl;

  // On OAuth return, restore link token from localStorage instead of
  // re-fetching (a new token would break the OAuth continuation flow).
  const [linkToken, setLinkTokenState] = useState<string | null>(() => {
    const stored = isOAuthReturn ? localStorage.getItem(RECONNECT_LINK_TOKEN_KEY) : null;
    console.log('[ReconnectPlaidPage] MOUNT', {
      url: window.location.href,
      isOAuthReturn,
      tokenIdFromUrl,
      effectiveTokenId: isOAuthReturn ? (tokenIdFromUrl || localStorage.getItem(RECONNECT_TOKEN_ID_KEY)) : tokenIdFromUrl,
      storedLinkTokenPrefix: stored?.slice(0, 30) ?? null,
      hasSession: !!session?.access_token,
      tenantId: selectedTenant?.id,
      allPlaidKeys: Object.keys(localStorage).filter(k => k.startsWith('xacto_plaid')),
    });
    return stored;
  });

  // No loading state needed on OAuth return — we already have the token.
  const [loading, setLoading] = useState(!isOAuthReturn);
  // Increment to re-trigger the link token fetch (e.g. after INVALID_LINK_TOKEN).
  const [retryCount, setRetryCount] = useState(0);

  // Plaid requires receivedRedirectUri = window.location.href on OAuth return
  // so it can extract oauth_state_id and correlate the session.
  const receivedRedirectUri = isOAuthReturn ? window.location.href : undefined;

  // Persists the link token to localStorage so it survives the OAuth
  // page reload (bank redirects → React state is lost).
  const setLinkToken = useCallback((token: string | null) => {
    setLinkTokenState(token);
    if (token) {
      localStorage.setItem(RECONNECT_LINK_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(RECONNECT_LINK_TOKEN_KEY);
      localStorage.removeItem(RECONNECT_TOKEN_ID_KEY);
      localStorage.removeItem(OAUTH_PENDING_KEY);
    }
  }, []);

  // Save tokenId to localStorage before opening Link so it survives the
  // bank's OAuth redirect (the redirect URL won't carry it back).
  useEffect(() => {
    if (tokenIdFromUrl && !isOAuthReturn) {
      localStorage.setItem(RECONNECT_TOKEN_ID_KEY, tokenIdFromUrl);
    }
  }, [tokenIdFromUrl, isOAuthReturn]);

  // Fetch update-mode link token. Skipped on OAuth return — the token is
  // already in localStorage from the previous render cycle.
  useEffect(() => {
    if (isOAuthReturn) return;

    const abortController = new AbortController();

    const fetchLinkToken = async () => {
      if (!session?.access_token) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiJson<{ link_token: string }>(
          "/api/link-token-update",
          {
            method: "POST",
            headers: {
              "x-tenant-id": selectedTenant?.id || "",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ tokenId: tokenIdFromUrl || undefined }),
            signal: abortController.signal,
          },
          session.access_token
        );

        if (abortController.signal.aborted) return;

        if (response.link_token) {
          // Save before opening Link so the state survives the OAuth bank redirect.
          const pendingState = {
            linkToken: response.link_token,
            mode: "reconnect",
            tokenId: tokenIdFromUrl ?? undefined,
            tenantId: selectedTenant?.id ?? "",
          };
          localStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify(pendingState));
          console.log('[ReconnectPlaidPage] update link token fetched, saved to localStorage', {
            tokenId: tokenIdFromUrl,
            tenantId: selectedTenant?.id,
            linkTokenPrefix: response.link_token?.slice(0, 30),
            redirectUri: `${window.location.origin}/plaid/oauth-return`,
          });
          setLinkToken(response.link_token);
          return;
        }

        throw new Error("No link token returned");
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error("Error fetching link token:", error);
        toast({
          title: "Error preparing reconnection",
          description: "Could not initialize Plaid Link. Please try again later.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    if (selectedTenant?.id && hasSession) {
      fetchLinkToken();
    }

    return () => abortController.abort();
  }, [selectedTenant?.id, hasSession, tokenIdFromUrl, isOAuthReturn, session?.access_token, setLinkToken, toast, retryCount]);

  const onSuccess = useCallback(
    async (publicToken: string, metadata: any) => {
      if (isExchangingRef.current) return;
      isExchangingRef.current = true;
      console.log('[ReconnectPlaidPage] onSuccess RECEIVED', {
        mode: 'reconnect',
        institution: metadata.institution?.name,
        institutionId: metadata.institution?.institution_id,
        effectiveTokenId,
        tenantId: selectedTenant?.id,
        hasSession: !!sessionRef.current?.access_token,
        linkSessionId: metadata.link_session_id,
      });
      try {
        if (!sessionRef.current?.access_token) {
          throw new Error("No access token available");
        }

        const response = await apiFetch(
          '/api/signup/exchange-token',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              publicToken,
              tenantId: selectedTenant?.id ?? '',
              institutionId: metadata.institution?.institution_id,
              institutionName: metadata.institution?.name,
              linkSessionId: metadata.link_session_id,
            }),
          },
          sessionRef.current.access_token
        );

        if (!response.ok) {
          throw new Error('Failed to exchange token on reconnect');
        }

        // Clear localStorage now that the flow is complete.
        localStorage.removeItem(RECONNECT_LINK_TOKEN_KEY);
        localStorage.removeItem(RECONNECT_TOKEN_ID_KEY);
        localStorage.removeItem(OAUTH_PENDING_KEY);

        toast({
          title: "Connection Restored",
          description: `Your ${metadata.institution?.name || "bank"} account has been successfully reconnected.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        navigate("/status");
      } catch (error) {
        console.error("Error updating token status:", error);
        toast({
          title: "Connection Failed",
          description: "Bank reconnected with Plaid but failed to update status. Please refresh the page.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        // Navigate anyway — Plaid side was successful, health checker will auto-heal.
        navigate("/status");
      }
    },
    [navigate, toast, selectedTenant?.id, effectiveTokenId]
  );

  const onExit: PlaidLinkOnExit = useCallback((err, metadata) => {
    if (err) {
      console.error("[ReconnectPlaidPage] onExit", {
        mode: 'reconnect',
        tenantId: selectedTenant?.id ?? null,
        error_type: err.error_type,
        error_code: err.error_code,
        error_message: err.error_message,
        display_message: err.display_message,
        request_id: metadata?.request_id,
        link_session_id: metadata?.link_session_id,
      });
      if (err.error_code === 'INVALID_LINK_TOKEN') {
        setLinkToken(null);
        setLoading(true);
        setRetryCount(c => c + 1);
        toast({
          title: 'Session expired',
          description: 'Your reconnection session expired. Getting a new one…',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      }
    }
  }, [setLinkToken, toast]);

  const { open, ready } = usePlaidLinkWithLogging({
    mode: 'reconnect',
    tenantId: selectedTenant?.id ?? '',
    token: linkToken ?? "",
    onSuccess,
    onExit,
    ...(receivedRedirectUri && { receivedRedirectUri }),
  });

  // Auto-open on OAuth return — the user already went through the bank; just
  // complete the Plaid Link handshake without requiring another button click.
  useEffect(() => {
    console.log('[ReconnectPlaidPage] usePlaidLink state', {
      ready,
      hasToken: !!linkToken,
      linkTokenPrefix: linkToken?.slice(0, 30),
      isOAuthReturn,
      receivedRedirectUri,
    });
    if (isOAuthReturn && ready && linkToken) {
      console.log('[ReconnectPlaidPage] opening Plaid Link (OAuth return)');
      open();
    }
  }, [isOAuthReturn, ready, linkToken, open]);

  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.300");

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="brand.secondary" thickness="4px" />
      </Center>
    );
  }

  // On OAuth return just show a spinner while Plaid Link auto-opens.
  if (isOAuthReturn) {
    return (
      <Center h="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.secondary" thickness="4px" />
          <Text color={textColor} fontSize="md">
            Completing bank reconnection…
          </Text>
        </VStack>
      </Center>
    );
  }

  const canConnect = !!selectedTenant?.id && !!linkToken;

  return (
    <Container maxW="container.md" py={20}>
      <VStack spacing={10} align="center" justify="center">
        <Heading
          as="h1"
          size="xl"
          textAlign="center"
          color={headingColor}
          fontWeight="extrabold"
          letterSpacing="tight"
        >
          Reconnect Bank Account
        </Heading>

        <Text
          fontSize="lg"
          textAlign="center"
          color={textColor}
          maxW="2xl"
          lineHeight="tall"
        >
          Your bank connection requires an update to continue syncing transactions.
          Please verify your credentials to restore secure access.
        </Text>

        <Box w="full" display="flex" justifyContent="center" pt={4}>
          {canConnect ? (
            <Button
              bg="brand.primary"
              color="white"
              _hover={{ bg: "brand.primaryDark" }}
              size="lg"
              px={8}
              py={6}
              fontSize="md"
              fontWeight="bold"
              borderRadius="full"
              shadow="lg"
              onClick={() => open()}
              isDisabled={!ready}
            >
              Launch Reconnection
            </Button>
          ) : (
            <VStack spacing={2}>
              {loading && <Spinner size="md" color="brand.secondary" />}
              {!loading && !selectedTenant && (
                <Text color="red.400" fontWeight="medium">
                  Unable to identify tenant. Please refresh or log in again.
                </Text>
              )}
              {!loading && selectedTenant && !linkToken && (
                <Text color="red.400" fontWeight="medium">
                  Failed to load connection data. Please refresh the page.
                </Text>
              )}
            </VStack>
          )}
        </Box>
      </VStack>
    </Container>
  );
};
